import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const base_schema = z.object({
  email: z.string().trim().min(3).max(160).regex(/^\S+$/),
  full_name: z.string().trim().min(1).max(160),
  status: z.enum(['active', 'inactive', 'suspended']),
  role_ids: z.array(z.string()).min(1),
});

const create_schema = base_schema.extend({
  password: z.string().min(8).max(128),
});

const update_schema = base_schema.extend({
  password: z.string().optional(),
});

export type CreateUserFormValues = z.infer<typeof create_schema>;
export type UpdateUserFormValues = z.infer<typeof update_schema>;

interface RoleOption {
  id: string;
  name: string;
  description: string | null;
}

interface UserFormDialogProps {
  mode: 'create' | 'update';
  title: string;
  role_options: ReadonlyArray<RoleOption>;
  initial_values?: Partial<UpdateUserFormValues>;
  submitting: boolean;
  onSubmit: (values: CreateUserFormValues | UpdateUserFormValues) => Promise<void>;
  onCancel: () => void;
}

function normalizeRoleName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_');
}

export function UserFormDialog({
  mode,
  title,
  role_options,
  initial_values,
  submitting,
  onSubmit,
  onCancel,
}: UserFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<CreateUserFormValues | UpdateUserFormValues>({
    resolver: zodResolver(mode === 'create' ? create_schema : update_schema),
    defaultValues: {
      email: initial_values?.email ?? '',
      full_name: initial_values?.full_name ?? '',
      status: initial_values?.status ?? 'active',
      role_ids: initial_values?.role_ids ?? [],
      ...(mode === 'create' ? { password: '' } : {}),
    },
  });

  const selected_role_ids = form.watch('role_ids') ?? [];

  const toggleRole = (role_id: string): void => {
    const current = new Set(selected_role_ids);
    if (current.has(role_id)) current.delete(role_id);
    else current.add(role_id);
    form.setValue('role_ids', [...current], { shouldDirty: true, shouldValidate: true });
  };

  const onSubmitForm = async (
    values: CreateUserFormValues | UpdateUserFormValues,
  ): Promise<void> => {
    await onSubmit(values);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <form onSubmit={form.handleSubmit(onSubmitForm)} noValidate>
          <CardHeader>
            <CardTitle className="font-display font-bold tracking-tight">{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">{t('users_admin.full_name')}</Label>
              <Input id="full_name" autoFocus maxLength={160} {...form.register('full_name')} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('users_admin.email')}</Label>
              <Input id="email" type="text" maxLength={160} {...form.register('email')} />
            </div>

            {mode === 'create' ? (
              <div className="space-y-2">
                <Label htmlFor="password">{t('users_admin.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  maxLength={128}
                  {...form.register('password')}
                />
                <p className="text-muted-foreground text-xs">{t('users_admin.password_hint')}</p>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="status">{t('users_admin.status')}</Label>
              <select
                id="status"
                {...form.register('status')}
                className="border-input bg-background focus:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
              >
                <option value="active">{t('status.active')}</option>
                <option value="inactive">{t('status.inactive')}</option>
                <option value="suspended">{t('users_admin.status_suspended')}</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label>{t('users_admin.roles')}</Label>
              <div className="border-border max-h-40 space-y-2 overflow-auto rounded-md border p-2">
                {role_options.map((role) => {
                  const checked = selected_role_ids.includes(role.id);
                  const role_key = normalizeRoleName(role.name);
                  const role_label = t(`users_admin.role_${role_key}`, { defaultValue: role.name });
                  return (
                    <label key={role.id} className="flex cursor-pointer items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={checked}
                        onChange={() => toggleRole(role.id)}
                      />
                      <span>
                        <span className="font-medium">{role_label}</span>
                        {role.description ? (
                          <span className="text-muted-foreground block text-xs">
                            {role.description}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {form.formState.errors.role_ids ? (
              <p className="text-destructive text-sm">{t('users_admin.role_required')}</p>
            ) : null}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
